import { AxiosRequestConfig, AxiosResponse } from "axios";
import client from "libs/HttpClient";

/**
 * Represents the structure of a request for listing users.
 */
interface ListUserRequest {
  queryKey: [number, string, string, string];
}
interface ListAllUserRequest {
  queryKey: [number, string, string, string];
}
/**
 * Represents the data required for admin login.
 */
interface LoginData {
  usernameOrEmail: string;
  password: string;
}

/**
 * Service class for handling users.
 */
class UserService {
  static listUser(request: ListUserRequest) {
    const page_no = request.queryKey[0];
    const searchText = request.queryKey[1];
    const status = request.queryKey[2];
    const user_type = request.queryKey[3];

    return client.get(
      `admin/user/list?page=${page_no}&search_text=${searchText}&status=${status}&user_type=${user_type}`,
      request as AxiosRequestConfig
    );
  }

  static listAllUser(request: ListAllUserRequest) {
    const page_no = request.queryKey[0];
    const searchText = request.queryKey[1];
    const status = request.queryKey[2];
    const user_type = request.queryKey[3];

    return client.get(
      `admin/user/user-list?page=${page_no}&search_text=${searchText}&status=${status}&user_type=${user_type}`,
      request as AxiosRequestConfig
    );
  }

  /**
   * Adds a new user based on the provided request.
   *
   * @param request - The request parameters for adding a user.
   * @returns A Promise that resolves to the Axios response for the add user request.
   */
  static addUser(request: any) {
    return client.post("admin/user/add", request);
  }

  /**
   * Updates the status of a user based on the provided request.
   *
   * @param request - The request parameters for updating the status of a user.
   * @returns A Promise that resolves to the Axios response for the update status request.
   */
  static updateStatusUser(request: any) {
    return client.patch("admin/user/update-status/", request);
  }

  /**
   * Updates the details of a user based on the provided request.
   *
   * @param request - The request parameters for updating a user.
   * @returns A Promise that resolves to the Axios response for the update user request.
   */
  static updateUser(request: any) {
    return client.post("admin/user/update/", request);
  }

  /**
   * Updates the details of a user based on the provided request.
   *
   * @param request - The request parameters for updating a user.
   * @returns A Promise that resolves to the Axios response for the update user request.
   */
  static updateUserByCreator(request: any) {
    return client.post("admin/user/user-update/", request);
  }

  /**
   * Retrieves the details of a user based on the provided parameters.
   *
   * @param params - The parameters for retrieving the user details.
   * @returns A Promise that resolves to the Axios response containing the user details.
   */
  static viewUser(params: any) {
    return client.get("admin/user/get-user", { params });
  }

  /**
   * Deletes a user based on the provided parameters.
   *
   * @param params - The parameters for deleting the user.
   * @returns A Promise that resolves to the Axios response for the delete user request.
   */
  static deleteUser(params: any) {
    return client.delete("/admin/user/delete/", { params });
  }

  /**
   * Performs admin login using the provided login data.
   *
   * @param loginData - The data containing email and password for authentication.
   * @returns A Promise that resolves to the AxiosResponse containing authentication results.
   */
  static loginUser(loginData: LoginData): Promise<AxiosResponse<any, any>> {
    return client.post("admin/user/login", loginData);
  }

  /**
   * Retrieves the details of a user based on the provided parameters.
   *
   * @param request - The parameters for retrieving the user details.
   * @returns A Promise that resolves to the Axios response containing the user details.
   */
  static viewUserDetails(request: any) {
    return client.get("admin/user/get-user-profile", request);
  }

  /**
   * Updates the password of a user based on the provided request.
   *
   * @param request - The request parameters for updating a password.
   * @returns A Promise that resolves to the Axios response for the update password request.
   */
  static userChangePassword(request: any) {
    return client.put("admin/user/change-password/", request);
  }

  /**
   * Verify OTP of a user based on the provided request.
   *
   * @param request - The request parameters for Verify Otp.
   * @returns A Promise that resolves to the Axios response for the Verify Otp request.
   */
  static otpVerify(request: any) {
    return client.put("admin/user/verify-otp/", request);
  }

  /**
   * Resend OTP of a user based on the provided request.
   *
   * @param request - The request parameters for Resend Otp.
   * @returns A Promise that resolves to the Axios response for the Resend Otp request.
   */
  static resendOtp(request: any) {
    return client.put("admin/user/resend-otp/", request);
  }
}

export { UserService };
